import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiSave, FiX, FiArrowLeft, FiTrash2, FiServer } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import theme from '../styles/theme';
import { parametrosWhatsappAPI } from '../services/api';
import Layout from '../components/Layout';
import { formatPhone } from '../utils/formatters';

const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing[8]};
`;

const TitleBlock = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.colors.background.tertiary};
    color: ${theme.colors.text.primary};
    border-color: ${theme.colors.border.hover};
  }
`;

const TitleInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled.h1`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  margin: 0;
`;

const Subtitle = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  margin: ${theme.spacing[1]} 0 0 0;
`;

const Actions = styled.div`
  display: flex;
  gap: ${theme.spacing[4]};
  justify-content: flex-end;
  padding-top: ${theme.spacing[6]};
  border-top: 1px solid ${theme.colors.border.primary};
  margin-top: ${theme.spacing[4]};
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  background: ${props => props.$primary ? theme.colors.primary[500] : props.$danger ? theme.colors.error[500] : 'transparent'};
  color: ${props => (props.$primary || props.$danger) ? theme.colors.text.primary : theme.colors.text.secondary};
  border: 1px solid ${props => props.$primary ? theme.colors.primary[500] : props.$danger ? theme.colors.error[500] : theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${props => props.$primary ? theme.colors.primary[600] : props.$danger ? theme.colors.error[600] : theme.colors.background.secondary};
    transform: ${props => (props.$primary || props.$danger) ? 'translateY(-1px)' : 'none'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FormContainer = styled.form`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[6]};
`;

const FormSection = styled.div`
  margin-bottom: ${theme.spacing[8]};

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.lg};
  font-weight: 600;
  margin: 0 0 ${theme.spacing[4]} 0;
  padding-bottom: ${theme.spacing[2]};
  border-bottom: 1px solid ${theme.colors.border.primary};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing[4]};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  grid-column: ${props => props.$fullWidth ? '1 / -1' : 'auto'};
`;

const Label = styled.label`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 500;

  span {
    color: ${theme.colors.error[500]};
    margin-left: ${theme.spacing[1]};
  }
`;

const Input = styled.input`
  padding: ${theme.spacing[3]};
  background: ${theme.colors.background.primary};
  border: 1px solid ${props => props.$error ? theme.colors.error[500] : theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.$error ? theme.colors.error[500] : theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${props => props.$error ? theme.colors.error[500] : theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.span`
  color: ${theme.colors.error[500]};
  font-size: ${theme.fontSizes.xs};
  margin-top: ${theme.spacing[1]};
`;

const MessageBar = styled.div`
  padding: ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.lg};
  margin-bottom: ${theme.spacing[6]};
  background: ${props => props.$type === 'error' ? `${theme.colors.error[500]}20` : `${theme.colors.success[500]}20`};
  color: ${props => props.$type === 'error' ? theme.colors.error[500] : theme.colors.success[500]};
  border: 1px solid ${props => props.$type === 'error' ? `${theme.colors.error[500]}40` : `${theme.colors.success[500]}40`};
  font-size: ${theme.fontSizes.sm};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const MessageClose = styled.button`
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: ${theme.spacing[1]};
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[6]};
  width: 100%;
  max-width: 400px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
`;

const ModalTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.lg};
  font-weight: 600;
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const ModalText = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  margin: 0 0 ${theme.spacing[6]} 0;
  line-height: 1.5;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${theme.spacing[3]};
`;


const ParametroWhatsappForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = id && id !== 'novo';

  const [formData, setFormData] = useState({
    ambiente: '',
    token: '',
    nome: '',
    contato: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    if (isEditing) {
      carregarParametro();
    }
  }, [id]);

  const carregarParametro = async () => {
    try {
      setLoading(true);
      const response = await parametrosWhatsappAPI.buscarPorId(id);
      
      if (response.data.success) {
        const parametro = response.data.data;
        setFormData({
          ambiente: parametro.ambiente || '',
          token: parametro.token || '',
          nome: parametro.nome || '',
          contato: parametro.contato ? formatPhone(parametro.contato) : ''
        });
      } else {
        setMessage({ type: 'error', text: response.data.error || 'Erro ao carregar parâmetro' });
      }
    } catch (err) {
      console.error('Erro ao carregar parâmetro:', err);
      setMessage({ type: 'error', text: 'Erro ao carregar os dados. Tente novamente.' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    let formattedValue = value;
    if (name === 'contato') {
      formattedValue = formatPhone(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: formattedValue
    }));

    // Limpar erro do campo recém alterado
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validarForm = () => {
    const novosErros = {};
    
    if (!formData.ambiente) novosErros.ambiente = 'Ambiente é obrigatório';
    if (!formData.token) novosErros.token = 'Token é obrigatório';

    setErrors(novosErros);
    return Object.keys(novosErros).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validarForm()) return;

    try {
      setSaving(true);
      setMessage(null);

      // Limpa a máscara do telefone para enviar apenas números para a API
      const contatoLimpo = formData.contato ? formData.contato.replace(/\D/g, '') : '';

      const dadosSalvar = {
        ambiente: formData.ambiente,
        token: formData.token,
        nome: formData.nome,
        contato: contatoLimpo
      };

      let response;

      if (isEditing) {
        response = await parametrosWhatsappAPI.atualizar(id, dadosSalvar);
      } else {
        response = await parametrosWhatsappAPI.criar(dadosSalvar);
      }

      if (response.data.success) {
        setMessage({ 
          type: 'success', 
          text: isEditing ? 'Parâmetro atualizado com sucesso!' : 'Parâmetro criado com sucesso!' 
        });
        
        // Redireciona para a lista após um breve delay
        setTimeout(() => {
          navigate('/parametros-whatsapp');
        }, 1500);
      } else {
        setMessage({ type: 'error', text: response.data.error || 'Erro ao salvar parâmetro' });
      }
    } catch (err) {
      console.error('Erro ao salvar:', err);
      setMessage({ 
        type: 'error', 
        text: err.response?.data?.error || 'Erro ao salvar os dados. Verifique tente novamente.' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setSaving(true);
      const response = await parametrosWhatsappAPI.deletar(id);
      
      if (response.data.success) {
        navigate('/parametros-whatsapp');
      } else {
        setMessage({ type: 'error', text: response.data.error || 'Erro ao deletar parâmetro' });
        setShowDeleteModal(false);
      }
    } catch (err) {
      console.error('Erro ao deletar:', err);
      setMessage({ type: 'error', text: 'Erro ao deletar o parâmetro.' });
      setShowDeleteModal(false);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout title={isEditing ? 'Editar Parâmetro' : 'Novo Parâmetro'}>
        <PageContainer>
          <div style={{ textAlign: 'center', padding: '40px', color: theme.colors.text.secondary }}>
            Carregando dados...
          </div>
        </PageContainer>
      </Layout>
    );
  }

  return (
    <Layout title={isEditing ? 'Editar Parâmetro' : 'Novo Parâmetro'}>
      <PageContainer>
        <Header>
          <TitleBlock>
            <BackButton type="button" onClick={() => navigate('/parametros-whatsapp')}>
              <FiArrowLeft />
            </BackButton>
            <TitleInfo>
              <Title>{isEditing ? 'Editar Parâmetro' : 'Novo Parâmetro'}</Title>
              <Subtitle>{isEditing ? 'Atualize as credenciais' : 'Cadastre novas credenciais'}</Subtitle>
            </TitleInfo>
          </TitleBlock>
        </Header>

        {message && (
          <MessageBar $type={message.type}>
            {message.text}
            <MessageClose type="button" onClick={() => setMessage(null)}>
              <FiX />
            </MessageClose>
          </MessageBar>
        )}

        <FormContainer onSubmit={handleSubmit}>
          <FormSection>
            <SectionTitle>
              <FiServer style={{ marginRight: theme.spacing[2], verticalAlign: 'middle' }} />
              Dados da Conexão
            </SectionTitle>
            <FormGrid>
              <FormGroup>
                <Label>Ambiente <span>*</span></Label>
                <Input
                  type="text"
                  name="ambiente"
                  value={formData.ambiente}
                  onChange={handleChange}
                  placeholder="Ex: evolution_default"
                  $error={errors.ambiente}
                />
                {errors.ambiente && <ErrorText>{errors.ambiente}</ErrorText>}
              </FormGroup>

              <FormGroup>
                <Label>Nome Interno</Label>
                <Input
                  type="text"
                  name="nome"
                  value={formData.nome || ''}
                  onChange={handleChange}
                  placeholder="Ex: WhatsApp Vendas"
                />
              </FormGroup>

              <FormGroup $fullWidth>
                <Label>Token Global <span>*</span></Label>
                <Input
                  type="text"
                  name="token"
                  value={formData.token}
                  onChange={handleChange}
                  placeholder="Token gerado pela Evolution API"
                  $error={errors.token}
                />
                {errors.token && <ErrorText>{errors.token}</ErrorText>}
              </FormGroup>

              <FormGroup>
                <Label>Contato Principal</Label>
                <Input
                  type="text"
                  name="contato"
                  value={formData.contato || ''}
                  onChange={handleChange}
                  placeholder="Ex: 5511999999999"
                />
              </FormGroup>
            </FormGrid>
          </FormSection>

          <Actions>
            <Button type="button" onClick={() => navigate('/parametros-whatsapp')}>
              Cancelar
            </Button>
            {isEditing && (
              <Button $danger onClick={() => setShowDeleteModal(true)} type="button">
                <FiTrash2 />
                Excluir
              </Button>
            )}
            <Button $primary type="submit" disabled={saving}>
              <FiSave />
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </Actions>
        </FormContainer>

        {showDeleteModal && (
          <ModalOverlay onClick={() => setShowDeleteModal(false)}>
            <ModalContent onClick={e => e.stopPropagation()}>
              <ModalTitle>Excluir Parâmetro</ModalTitle>
              <ModalText>
                Tem certeza que deseja excluir o parâmetro <strong>{formData.ambiente}</strong>? 
                Esta ação não pode ser desfeita.
              </ModalText>
              <ModalActions>
                <Button type="button" onClick={() => setShowDeleteModal(false)}>Cancelar</Button>
                <Button type="button" $danger onClick={handleDelete} disabled={saving}>
                  {saving ? 'Excluindo...' : 'Sim, excluir parâmetro'}
                </Button>
              </ModalActions>
            </ModalContent>
          </ModalOverlay>
        )}
      </PageContainer>
    </Layout>
  );
};

export default ParametroWhatsappForm;
